import * as R from 'remeda';
import { IntlShape } from 'react-intl';
import { DatapointAutomationBlocker } from '@rossum/api-client/automationBlockers';
import {
  isSchemaDatapoint,
  SchemaUiConfigType,
} from '@rossum/api-client/schemas';
import { Message } from '@rossum/api-client/shared';
import {
  AUTOMATION_BUILT_IN_CHECKS,
  CONFIDENCE_THRESHOLDS_LINK,
} from '../../../../../constants/values';
import { schemaMapSelector } from '../../../../../redux/modules/schema/schemaMapSelector';
import { MessagesStats } from '../../../../../types/datapoints';
import {
  AnyDatapointSchema,
  EnumDatapointSchema,
} from '../../../../../types/schema';

export const BLOCKERS_COLOR = 'warning';

export type MessageOrBlocker = Omit<Message, 'type'> & {
  type: Message['type'] | 'blocker';
  docsLink?: string;
  detail?:
    | Message['detail']
    | DatapointAutomationBlocker['samples'][0]['details'];
};

const docLinksMap: Partial<Record<DatapointAutomationBlocker['type'], string>> =
  {
    low_score: CONFIDENCE_THRESHOLDS_LINK,
    failed_checks: AUTOMATION_BUILT_IN_CHECKS,
  };

export const createContentForDatapointBlocker = (
  blocker: DatapointAutomationBlocker,
  sample: DatapointAutomationBlocker['samples'][0] | undefined,
  intl: IntlShape
) => {
  switch (blocker.type) {
    case 'error_message': {
      const message = sample?.details?.messageContent?.join(', ');
      return `${intl.formatMessage({
        id: `components.documentValidation.automationBlockers.tooltip.error_message`,
      })}${message ? ` ${message}` : ''}`;
    }

    case 'failed_checks': {
      return intl.formatMessage({
        id: `components.documentValidation.automationBlockers.tooltip.failed_checks`,
      });
    }

    case 'low_score': {
      const score = sample?.details?.score;
      const threshold = sample?.details?.threshold;

      return R.isDefined(score) && R.isDefined(threshold)
        ? intl.formatMessage(
            {
              id: 'components.documentValidation.automationBlockers.tooltip.low_score_dynamic_threshold',
            },
            {
              confidencePct: `${parseFloat((score * 100).toFixed(2))}%`,
              thresholdPct: `${parseFloat((threshold * 100).toFixed(2))}%`,
            }
          )
        : intl.formatMessage({
            id: 'components.documentValidation.automationBlockers.tooltip.low_score_threshold',
          });
    }

    case 'extension': {
      const content = sample?.details?.content?.join(', ');

      return `${intl.formatMessage({
        id: 'components.documentValidation.automationBlockers.tooltip.extension',
      })}${content ? `: ${content}` : ''}`;
    }

    case 'no_validation_sources': {
      return intl.formatMessage({
        id: `components.documentValidation.automationBlockers.tooltip.no_validation_sources`,
      });
    }

    default: {
      return `${intl.formatMessage({
        id: 'components.documentValidation.automationBlockers.tooltip.other',
        // This should never happen as all cases are handled above
      })}: ${blocker.type as string}`;
    }
  }
};

// prepend the label of the column for given blocker type,
// ignore the content of it because there can be multiple samples
// in footer we display the content in the context of given datapoint, we do not need that in sidebar
const createContentForLineItemBlocker = (
  blocker: DatapointAutomationBlocker,
  schemaMap: ReturnType<typeof schemaMapSelector>,
  intl: IntlShape
) => {
  const fieldLabel = schemaMap.get(blocker.schemaId)?.label ?? '';

  switch (blocker.type) {
    case 'error_message':
    case 'extension':
    case 'failed_checks':
    case 'no_validation_sources': {
      return `${fieldLabel}: ${intl.formatMessage({
        id: `components.documentValidation.automationBlockers.tooltip.${blocker.type}`,
      })}`;
    }

    case 'low_score': {
      return `${fieldLabel}: ${intl.formatMessage({
        id: 'components.documentValidation.automationBlockers.tooltip.low_score_threshold',
      })}`;
    }

    default: {
      return `${fieldLabel}: ${intl.formatMessage({
        id: 'components.documentValidation.automationBlockers.tooltip.other',
        // This should never happen as all cases are handled above
      })}: ${blocker.type as string}`;
    }
  }
};

export const groupMessagesByType = (
  messages: Array<Message> | undefined | null
) => {
  const [errorMessages, warningAndInfoMessages] = R.partition(
    messages ?? [],
    message => message.type === 'error'
  );

  const [warningMessages, infoMessages] = R.partition(
    warningAndInfoMessages,
    message => message.type === 'warning'
  );
  return { errorMessages, warningMessages, infoMessages };
};

export const combineMessagesAndBlockers = (
  currentSchemaId: string,
  messages: Array<Message> | undefined = [],
  blockers: Array<DatapointAutomationBlocker> | undefined = [],
  schemaMap: ReturnType<typeof schemaMapSelector>,
  intl: IntlShape
): Array<MessageOrBlocker> => {
  const { errorMessages, warningMessages, infoMessages } =
    groupMessagesByType(messages);

  const samplesAsMessages = blockers.map(
    blocker =>
      ({
        type: 'blocker',
        content:
          // multivalues can have different schemaId than the current one
          currentSchemaId !== blocker.schemaId
            ? createContentForLineItemBlocker(blocker, schemaMap, intl)
            : createContentForDatapointBlocker(
                blocker,
                blocker.samples[0],
                intl
              ),
        docsLink: docLinksMap[blocker.type],
        detail: blocker.samples[0]?.details?.detail?.[0],
      }) satisfies MessageOrBlocker
  );

  // order of types should be: errors, blockers, warnings, infos
  // reverse the order of messages because we want to display the latest messages first
  return [
    ...R.reverse(errorMessages),
    ...samplesAsMessages,
    ...R.reverse(warningMessages),
    ...R.reverse(infoMessages),
  ];
};

const MAX_TOOLTIP_COLS = 3;

// A descendant message paired with the column label it came from.
// Used to build aggregated tooltips on line-item / multivalue parent rows,
// where the parent datapoint itself carries no direct messages.
export type LabeledMessage = { label: string; message: Message };

// Format a tooltip string for a line-item/multivalue parent row from the
// messages collected on its descendant columns.
// Rules:
//   - 1 distinct message body: list up to 3 col names + that body
//     e.g. "Code, Total, Due Date: Field is required"
//     or   "Code, Total, Due Date and 2 more: Field is required"
//   - 2+ distinct message bodies: show first body's cols + "and N more errors"
//     e.g. "Code: Field is required and 2 more errors"
export const buildDescendantTooltip = (
  items: LabeledMessage[],
  intl: IntlShape
): string | undefined => {
  // Drop messages without a body: empty content has nothing to describe and
  // would otherwise group under '' (a valid Map key), producing a label-only
  // "Code: " line and inflating the "and N more errors" count.
  const nonEmpty = items.filter(({ message }) => !!message.content);
  if (nonEmpty.length === 0) return undefined;

  // Group distinct column labels by message body; preserves first-seen order.
  const groups = nonEmpty.reduce((acc, { label, message }) => {
    const body = message.content;
    const cols = acc.get(body) ?? [];
    if (label && !cols.includes(label)) cols.push(label);
    acc.set(body, cols);
    return acc;
  }, new Map<string, string[]>());

  const entries = [...groups.entries()];
  const firstEntry = entries[0];
  if (!firstEntry) return undefined;
  const [firstBody, firstCols] = firstEntry;
  const rest = entries.slice(1);

  const shown = firstCols.slice(0, MAX_TOOLTIP_COLS);
  const hiddenCols = firstCols.length - shown.length;
  const moreColsSuffix =
    hiddenCols > 0
      ? ` ${intl.formatMessage({ id: 'components.sidebarV2.messageIndicator.moreColumns' }, { count: hiddenCols })}`
      : '';
  const colList =
    shown.length > 0 ? `${shown.join(', ')}${moreColsSuffix}` : '';
  const firstPart = colList ? `${colList}: ${firstBody}` : firstBody;

  if (rest.length === 0) return firstPart;

  // Count of additional *distinct* error bodies, not total extra columns.
  const moreErrorsSuffix = intl.formatMessage(
    { id: 'components.sidebarV2.messageIndicator.moreErrors' },
    { count: rest.length }
  );
  return `${firstPart} ${moreErrorsSuffix}`;
};

export type MessagesStatsWithBlockers = Omit<
  MessagesStats,
  'highestSeverity'
> & {
  highestSeverity: MessagesStats['highestSeverity'] | 'blocker';
  tooltipContent?: string;
};

export const combineMessagesStatsAndBlockers = (
  messagesStats: MessagesStats | undefined,
  blockers: Array<DatapointAutomationBlocker> | undefined,
  messages: MessageOrBlocker[] = []
): MessagesStatsWithBlockers | undefined => {
  const currentSeverity = messagesStats?.highestSeverity;
  const newCount = (messagesStats?.count ?? 0) + (blockers?.length ?? 0);
  const newSeverity =
    currentSeverity === 'error'
      ? 'error'
      : blockers?.length
        ? 'blocker'
        : currentSeverity;

  if (newSeverity === undefined || newCount === 0) return undefined;

  const uniqueContents = new Set(messages.map(m => m.content).filter(Boolean));
  const tooltipContent =
    uniqueContents.size === 1 ? [...uniqueContents][0] : undefined;

  return { count: newCount, highestSeverity: newSeverity, tooltipContent };
};

export const resolveValueSource = <
  DP extends {
    validationSources: string[];
    schema?: AnyDatapointSchema | EnumDatapointSchema;
  },
>(
  dp: DP
): SchemaUiConfigType | 'unset' | 'einvoicing' => {
  // exception for einvoicing as its type can be set as captured or data type
  // we want to override it from extension and mark it as einvoicing
  if (dp.validationSources.includes('einvoicing')) {
    return 'einvoicing';
  }

  if (isSchemaDatapoint(dp.schema)) {
    return dp.schema.uiConfiguration?.type ?? ('unset' as const);
  }

  return 'unset';
};
